import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { TextFieldInput } from '../../../inputs/text-field-input'
import { getErrorMessages } from '../../../utils/error-mapping'
import { ReportForm, ReportFormData } from '../report-form'
import { usePopulatedForm } from '../report-form/use-populated-form'
import { ForceWarehouseFilter } from './force-warehouse-filter'
import { useTracking } from '../../../hooks/tracking'

type Props = {
  dialogData: ReportFormData
  title: string
  name: string
  isLoading: boolean
  onSave: (formData: ReportForm) => Promise<void>
}

export const ManageTemplateDialog = ({
  name,
  title,
  dialogData,
  isLoading,
  onSave,
}: Props) => {
  const navigate = useNavigate()
  const { t } = useTranslation(['reports', 'shared'])
  const { formContext } = usePopulatedForm({ dialogData, name })
  const { trackFormSuccess, trackFormError } = useTracking()

  const handleClose = () =>
    void navigate('..', { replace: true, relative: 'path' })

  const handleSubmit = async (formData: ReportForm) => {
    try {
      await onSave(formData)
      enqueueSnackbar(t('manage_template_dialog.success_save_template'), {
        variant: 'success',
      })
      handleClose()
      trackFormSuccess({
        name: 'manage-template-dialog',
        forceWarehouseFilter: formData.forcewarehousefilter,
      })
    } catch (error) {
      enqueueSnackbar(getErrorMessages(error)[0], { variant: 'error' })
      trackFormError({ name: 'manage-template-dialog' })
    }
  }

  return (
    <FormProvider {...formContext}>
      <Dialog open onClose={handleClose} fullWidth maxWidth="xs" sx={{ p: 2 }}>
        <DialogTitle>{title}</DialogTitle>

        <DialogContent sx={{ '.MuiListItem-root': { px: 0 } }}>
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextFieldInput
              label={t('manage_template_dialog.name')}
              name="reportname"
              control={formContext.control}
              required
            />

            <TextFieldInput
              label={t('manage_template_dialog.description')}
              name="reportdescription"
              control={formContext.control}
            />

            <ForceWarehouseFilter reportType={dialogData?.reporttype} />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button color="secondary" size="small" onClick={handleClose}>
            {t('shared:action.cancel')}
          </Button>
          <Button
            size="small"
            onClick={formContext.handleSubmit(handleSubmit)}
            loading={isLoading}
          >
            {t('shared:action.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  )
}
